/* eslint-disable max-lines */

import { addCommentEnvelope, fetchReviewers, setCommentError } from 'actions';
import LeavePageModal from 'components/LeavePageModal';
import moment from 'moment';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCommentError } from 'selectors/envelopeReview';
import { getAddCommentLoading, getSingleEnvelopeComments } from 'selectors/envelopes';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';

import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';

import { $generateHtmlFromNodes } from '@lexical/html';
import { $getRoot } from 'lexical';
import { getUsers } from 'selectors/users';
import { CommentWithUsers } from 'types';
import ClearEditorPlugin from 'utils/lexical/ClearEditorPlugin';
import ExecuteOnEnterPlugin from 'utils/lexical/ExecuteOnEnterPlugin';
import HandleModifyMentionsPlugin from 'utils/lexical/HandleModifyMentionsPlugin';
import LoadHtmlPlugin from 'utils/lexical/LoadHtmlPlugin';
import { MentionNode } from 'utils/lexical/MentionNode';
import MentionsPlugin from 'utils/lexical/MentionsPlugin';
import EnvelopeSidebarCommentItem from './EnvelopeSidebarCommentItem';

type ComponentProps = {
  setComment: Dispatch<SetStateAction<CommentWithUsers>>;
  comment: CommentWithUsers;
};

const EnvelopeSidebarComments: React.FC<ComponentProps> = ({ setComment, comment }) => {
  const dispatch = useDispatch();
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const [editingId, setEditingId] = useState('');

  const clearRef = useRef<React.ElementRef<typeof ClearEditorPlugin>>(null);

  const loading = useSelector(getAddCommentLoading);
  const commentError = useSelector(getCommentError);
  const comments = useSelector((state) => getSingleEnvelopeComments(state, envelopeId));
  const commentsRef = useRef<HTMLDivElement>(null);
  const users = useSelector(getUsers);

  useEffect(() => {
    dispatch(fetchReviewers({ searchValue: '', limit: -1 }));
  }, [dispatch]);

  useEffect(() => {
    if (commentError) {
      commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [commentError]);

  useEffect(() => {
    if (comments.length > 0) {
      dispatch(setCommentError(false));
    }
  }, [comments.length, dispatch]);

  useEffect(() => {
    dispatch(setCommentError(false));
  }, [envelopeId, dispatch]);

  const handleSaveComment = useCallback(
    (value?: string): void => {
      logEvent('envelope-add-comment');

      const val = value || comment.value;
      if (!val.trim() || editingId) {
        return;
      }

      dispatch(
        addCommentEnvelope({
          envelopeId,
          value: val,
          taggedUsers: comment.taggedUsers,
          textValue: comment.text,
        })
      );

      clearRef.current?.clear();
      setComment({ value: '', text: '', taggedUsers: [] });
      dispatch(setCommentError(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, comment, clearRef]
  );

  useEffect(() => {
    clearRef.current?.clear();
  }, [envelopeId]);

  const initialConfig: InitialConfigType = {
    namespace: 'main-comment-editor',
    nodes: [MentionNode],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any): void => {
      throw error;
    },
  };

  const renderComment = comments
    .filter((comm) => comm.deleted_at === null)
    .sort((c1, c2) => (moment(c1.created_at).isBefore(moment(c2?.created_at)) ? 1 : -1))
    .map((item, index, arr) => (
      <EnvelopeSidebarCommentItem
        key={item.uuid}
        comment={item}
        index={index}
        arr={arr}
        editingId={editingId}
        setEditingId={setEditingId}
      />
    ));

  const navigateAction = (): void => {
    logEvent('envelope-add-comment');
    dispatch(
      addCommentEnvelope({
        envelopeId,
        value: comment.value,
        taggedUsers: comment.taggedUsers,
        textValue: comment.text,
      })
    );
  };

  const handleCancelComment = (): void => {
    clearRef.current?.clear();
    setComment({ value: '', text: '', taggedUsers: [] });
  };

  const handleCreateMention = (name: string, email: string, val: string): void => {
    setComment((c) => ({
      value: c.value,
      text: c.text,
      taggedUsers: [
        ...c.taggedUsers.filter((u) => u.uuid !== val),
        {
          name,
          email,
          uuid: val,
        },
      ],
    }));
  };

  const handleDeleteMention = (val: string): void => {
    setComment((c) => ({
      value: c.value,
      text: c.text,
      taggedUsers: c.taggedUsers.filter((u) => u.uuid !== val),
    }));
  };

  const addCommentSection = (
    <div ref={commentsRef} className="flex flex-col gap-2 pl-3 pr-4">
      <div className="relative">
        <LexicalComposer initialConfig={initialConfig}>
          <LoadHtmlPlugin content="" />
          <MentionsPlugin
            initialOptions={Object.values(users).map((user) => ({
              name: user.name || '',
              email: user.email || '',
              value: user.uuid || '',
            }))}
          />
          <ClearEditorPlugin ref={clearRef} />
          <HandleModifyMentionsPlugin
            createdCallback={handleCreateMention}
            destroyedCallback={handleDeleteMention}
          />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                aria-placeholder="Hit 'Enter' to save"
                placeholder={
                  <div className="absolute italic" style={{ bottom: 4, right: 8 }}>
                    Hit &apos;Enter&apos; to save
                  </div>
                }
                className="form-input min-h-20 max-h-20 p-2 text-body rounded border border-litlingo-gray-2 overflow-auto custom-scrollbar"
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin
            onChange={(_editorState, editor): void => {
              editor.update(() => {
                const htmlString = $generateHtmlFromNodes(editor);
                const stringifiedEditorState = JSON.stringify(editor.getEditorState().toJSON());
                const parsedEditorState = editor.parseEditorState(stringifiedEditorState);
                const textContent = parsedEditorState.read(() => $getRoot().getTextContent());
                setComment((c) => ({
                  value: htmlString,
                  text: textContent,
                  taggedUsers: c.taggedUsers,
                }));
              });
            }}
          />
          <ExecuteOnEnterPlugin callback={handleSaveComment} />
        </LexicalComposer>
      </div>
      {commentError && (
        <div className="flex flex-row gap-1 items-center text-body">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842861 3.84344 0 5.87827 0 8C0 10.1217 0.842861 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16C10.1216 15.9996 12.1562 15.1567 13.6564 13.6564C15.1567 12.1562 15.9996 10.1216 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0ZM11.1743 10.578C11.2529 10.6568 11.297 10.7635 11.297 10.8747C11.297 10.986 11.2529 11.0927 11.1743 11.1714C11.0956 11.2502 10.9889 11.2945 10.8775 11.2946C10.7662 11.2945 10.6595 11.2502 10.5808 11.1714L8.0056 8.59622L5.42898 11.1714C5.35031 11.2502 5.24359 11.2945 5.13227 11.2946C5.02095 11.2945 4.91422 11.2502 4.83556 11.1714C4.75693 11.0927 4.71276 10.986 4.71276 10.8747C4.71276 10.7635 4.75693 10.6568 4.83556 10.578L7.41218 8.0028L4.83556 5.42617C4.75693 5.34745 4.71276 5.24073 4.71276 5.12946C4.71276 5.0182 4.75693 4.91148 4.83556 4.83275C4.91428 4.75412 5.021 4.70996 5.13227 4.70996C5.24353 4.70996 5.35025 4.75412 5.42898 4.83275L8.0056 7.40938L10.5808 4.83275C10.6596 4.75412 10.7663 4.70996 10.8775 4.70996C10.9888 4.70996 11.0955 4.75412 11.1743 4.83275C11.2529 4.91148 11.297 5.0182 11.297 5.12946C11.297 5.24073 11.2529 5.34745 11.1743 5.42617L8.59902 8.0028L11.1743 10.578Z"
              fill="#F8351A"
            />
          </svg>
          <span>An escalated status requires a comment</span>
        </div>
      )}
      <div className="flex flex-row justify-end gap-1">
        <button
          type="button"
          onClick={handleCancelComment}
          className="h-6 button button--secondary flex justify-center"
        >
          <span className="font-bold">Cancel</span>
        </button>
        <button
          type="button"
          onClick={(): void => handleSaveComment()}
          className="h-6 button button--primary"
          disabled={!!editingId}
        >
          Save
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col pt-3 gap-2 bg-white border border-litlingo-gray-1">
        {addCommentSection}

        <div className="flex flex-col gap-1 pt-2 pb-3 overflow-y-auto litlingo-envelope-comment-box pl-3 pr-4">
          {loading && (
            <div className="relative pb-1 pl-1 pr-2 flex flex-col gap-1 rounded bg-litlingo-gray-0.5">
              <div className="flex flex-col gap-1">
                <div className="flex flex-col gap-0.5">
                  <div className="h-5">
                    <SkeletonTheme color="#D6D6D6" highlightColor="#DCDCDC">
                      <Skeleton width={250} height={20} style={{ borderRadius: 0 }} />
                    </SkeletonTheme>
                  </div>
                  <div className="h-5">
                    <SkeletonTheme color="#EBEBEB" highlightColor="#EFEFEF">
                      <Skeleton width={288} height={20} style={{ borderRadius: 0 }} />
                    </SkeletonTheme>
                  </div>
                </div>
                <div className="self-end h-5">
                  <SkeletonTheme color="#EBEBEB" highlightColor="#EFEFEF">
                    <Skeleton width={152} height={16} style={{ borderRadius: 0 }} />
                  </SkeletonTheme>
                </div>
              </div>
            </div>
          )}
          {comments.length > 0 ? (
            renderComment
          ) : (
            <span className="text text--large">There are no comments yet</span>
          )}
        </div>
      </div>
      <LeavePageModal
        shouldBlockNavigation={!!comment.text}
        title="Save Comment?"
        text="Do you want to save your comment before moving to the next message?"
        navigateButtontext="No, Continue"
        cancelOrActionNavigateButtontext="Yes, Save Comment"
        navigateAction={navigateAction}
      />
    </>
  );
};

export default EnvelopeSidebarComments;
